import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpEventType,
} from '@angular/common/http';
import { Observable, throwError, TimeoutError, timer } from 'rxjs';
import { catchError, finalize, tap, concatMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { GlobalService } from '../services/global.service';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { TranslateService } from '@ngx-translate/core';
import { NetworkStatusService } from '../services/network-status.service';
import { AlertService } from '../services/alert.service';

/** Configurações de retry - Versão Segura */
const RETRY_CONFIG = {
  maxRetries: 3, // Tenta apenas 3 vezes. Se falhar à 3ª, para.
  initialDelayMs: 2000, // Mantém os 2 segundos iniciais
  maxDelayMs: 10000, // Baixa para 10 segundos (30s é muito tempo de espera)
  backoffMultiplier: 2, // Mantém o multiplicador
};

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  /** Flag para evitar múltiplas notificações de offline */
  private offlineNotified = false;
  /** Evita spam de logout/popups em 401s simultâneos */
  private isLoggingOut = false;
  /** Impede que o aviso non-blocking de 3G/2G sobrecarregue visualmente o ecrã com múltiplos HTTP requests independentes */
  private slowNetNotified = false;

  constructor(
    private globalService: GlobalService,
    private authService: AuthService,
    private translate: TranslateService,
    private networkStatusService: NetworkStatusService,
    private alert: AlertService,
  ) {
    // Quando a rede volta, resetar o flag de notificação
    this.networkStatusService.isOnline$.subscribe(isOnline => {
      if (isOnline) {
        this.offlineNotified = false;
      }
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1) Ignorar completamente pedidos de traduções (estáticos)
    const isI18n = /\/assets\/i18n\/.+\.json(\?|$)/.test(request.url);
    if (isI18n) {
      // passa direto, sem headers extra, sem parâmetros, sem contadores/spinners
      return next.handle(request);
    }

    // 2) Ignorar chamadas ao chatbot (API externa com headers próprios)
    if (request.url.includes('/webhook/api')) {
      console.log('🤖 [HttpInterceptor] Chatbot detectado — a ignorar interceptor para: ' + request.url);
      console.log(
        '🤖 [HttpInterceptor] Headers do pedido: ' +
          JSON.stringify(
            request.headers.keys().reduce((acc: any, k: string) => {
              acc[k] = request.headers.get(k);
              return acc;
            }, {}),
          ),
      );
      return next.handle(request).pipe(
        finalize(() => {
          // Manter contadores consistentes
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('🤖 [HttpInterceptor] Erro no chatbot — status: ' + error.status + ' url: ' + error.url);
          return throwError(() => error);
        }),
      );
    }

    // 3) Resto do fluxo normal para API, etc.
    const clonedRequest = this.prepareRequest(request);

    this.handleConnectionStatus();

    if (typeof window !== 'undefined' && !navigator.onLine) {
      this.showInternetConnectionError();
      return this.handleOfflineError();
    }

    this.globalService.activeRequests++;

    return this.handleRequestWithRetry(clonedRequest, next).pipe(
      finalize(() => {
        this.globalService.activeRequests = Math.max(0, this.globalService.activeRequests - 1);
      }),
    );
  }

  /** Quando não há internet: mostra aviso (já traduzido) e devolve um HttpErrorResponse "offline". */
  private handleOfflineError(): Observable<never> {
    const translatedMsg = this.translate.instant('ERRORS.NO_INTERNET');

    const offlineErr = new HttpErrorResponse({
      status: 0, // 0 = offline / network error
      statusText: 'OFFLINE',
      error: {
        code: 'OFFLINE',
        message: translatedMsg,
      },
    });

    return throwError(() => offlineErr);
  }

  private prepareRequest(request: HttpRequest<any>): HttpRequest<any> {
    const isThirdPartyApi = request.url.startsWith('http://') || request.url.startsWith('https://');
    let isOurDomain = !isThirdPartyApi;
    if (isThirdPartyApi) {
      try {
        const origin =
          typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin
            : 'https://segurosvirtual.pt';
        const hostname = new URL(request.url, origin).hostname.toLowerCase();
        isOurDomain =
          hostname === 'segurosvirtual.pt' ||
          hostname.endsWith('.segurosvirtual.pt') ||
          hostname === 'localhost' ||
          hostname === '127.0.0.1';
      } catch {
        isOurDomain = false;
      }
    }

    if (isThirdPartyApi && !isOurDomain) {
      // Pedidos a APIs públicas externas (ex: OpenStreetMap, BigDataCloud)
      // NUNCA anexar headers internos nem withCredentials para não violar CORS do browser
      return request;
    }

    // 🛡️ Rastreabilidade Forense: Injetar Request-ID único por pedido (Art. 8.º RCM 41/2018)
    const requestId = 'req_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 8);

    let headers = request.headers
      .set('Cache-Control', 'no-cache')
      .set('Pragma', 'no-cache')
      .set('Expires', '0')
      .set('X-Request-Timeout', '10000')
      .set('X-Request-Id', requestId);

    // ? SEGURANÇA: Continuar a enviar token no header para compatibilidade
    // O backend aceita tanto cookie (prioritário) como header
    if (this.globalService.loggedUserClass?.Token) {
      //   console.log(
      //     '?? [Interceptor] Anexando Token ao request:',
      //     request.url,
      //     '(Token len:',
      //     this.globalService.loggedUserClass.Token.length,
      //     ')',
      //   );
      headers = headers.set('x-access-token', this.globalService.loggedUserClass.Token);
    } else {
      const publicEndpoints = [
        'GetUserAuth',
        'GoogleAuth',
        'GoogleConfig',
        'Autenticate',
        'Version',
        'api/common/',
        'api/products/',
        'api/companies/',
        'api/payments/',
        'api/insurancepolicy/',
        'api/safesimulations/',
        'api/messagecenter/',
        'api/compaigns/',
        'api/blog/',
        'blog/',
        'api/homepage-layout/',
        'homepage-layout/',
        'read-claim-auto',
        'openai/read-claim-auto',
        'insuranceclaims/',
      ];
      const isPublicEndpoint = publicEndpoints.some(ep => request.url.includes(ep));
      if (!isPublicEndpoint) {
        console.warn('?? [Interceptor] Request SEM Token (User logged out?):', request.url);
      }
    }

    const user: any = this.globalService.loggedUserClass;
    const idUser = user?.idUser;
    const rawCommId = Number(
      (!user?.commercialClass?.Default && user?.commercialClass?.idCommercial) ||
        (this.globalService as any).selectedCommercialClass?.idCommercial ||
        0,
    );
    const idComm = rawCommId > 1 ? rawCommId : 0;
    const mediatorNif = String(user?.customerClass?.NIF || user?.NIF || user?.commercialClass?.NIF || '').trim();
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isMediadorProfile = Boolean(
      mediatorNif === '212915665' ||
      (idComm > 1 && !user?.commercialClass?.Default && !user?.Admin && !user?.SiteManagement),
    );
    const isCrmMediadorRoute =
      pathname.startsWith('/crm-mediador') ||
      (pathname.startsWith('/crm') && !pathname.startsWith('/crm/pedidos') && isMediadorProfile);

    if (isCrmMediadorRoute) {
      headers = headers.set('x-crm-mediador', '1');
      if (idComm > 1) {
        headers = headers.set('x-id-commercial', String(idComm));
      }
      if (mediatorNif) {
        headers = headers.set('x-mediator-nif', mediatorNif);
      }
    }

    // ? SEGURANÇA: withCredentials permite enviar HttpOnly cookies automaticamente
    const withCredentialsOption = { headers, withCredentials: true };

    if (request.method === 'POST' || request.method === 'PUT') {
      // Se o body for FormData, clonamos corretamente e adicionamos o idUserLogged
      if (request.body instanceof FormData) {
        const formData = new FormData();
        request.body.forEach((value: any, key: string) => {
          if (typeof value !== 'string') {
            if (value instanceof File) {
              formData.append(key, value, value.name);
            } else if (value instanceof Blob) {
              formData.append(key, value, 'blob.bin');
            } else {
              formData.append(key, value);
            }
          } else {
            formData.append(key, value);
          }
        });
        formData.append('idUserLogged', idUser != null ? String(idUser) : '0');
        if (isCrmMediadorRoute) {
          formData.append('mediatorScope', '1');
          if (idComm > 0) {
            formData.append('idCommercial', String(idComm));
          }
          if (mediatorNif) {
            formData.append('mediatorNif', mediatorNif);
          }
        }

        return request.clone({ ...withCredentialsOption, body: formData });
      }

      // Para JSON/objetos normais (excluindo Array e Blob), fazemos merge com idUserLogged prioritário
      if (
        !Array.isArray(request.body) &&
        !(request.body instanceof Blob) &&
        !(request.body instanceof ArrayBuffer) &&
        typeof request.body !== 'string'
      ) {
        const newBody: any = {
          ...(request.body && typeof request.body === 'object' ? request.body : {}),
          ...(isCrmMediadorRoute ? { mediatorScope: '1' } : {}),
          ...(isCrmMediadorRoute && idComm > 0 ? { idCommercial: idComm } : {}),
          ...(isCrmMediadorRoute && mediatorNif ? { mediatorNif } : {}),
          idUserLogged: idUser,
        };

        return request.clone({ ...withCredentialsOption, body: newBody });
      }

      return request.clone(withCredentialsOption);
    }

    if (request.method === 'GET') {
      let params = request.params;
      if (idUser != null) {
        params = params.set('idUserLogged', String(idUser));
      }
      if (isCrmMediadorRoute) {
        params = params.set('mediatorScope', '1');
        if (idComm > 0 && !params.has('idCommercial')) {
          params = params.set('idCommercial', String(idComm));
        }
        if (mediatorNif && !params.has('mediatorNif')) {
          params = params.set('mediatorNif', mediatorNif);
        }
      }
      return request.clone({ ...withCredentialsOption, params });
    }

    return request.clone(withCredentialsOption);
  }

  private handleConnectionStatus() {
    if (typeof window === 'undefined') return;
    const conn = (navigator as any).connection;
    if (conn) {
      const effectiveType = conn.effectiveType;
      if (['slow-2g', '2g', '3g'].includes(effectiveType)) {
        if (!this.slowNetNotified) {
          this.slowNetNotified = true;
          // Alerta muito leve e não impeditivo (Toast) em vez do Modal bloqueante antigo
          this.alert.showToastInfo(this.translate.instant('ERRORS.SLOW_INTERNET', { type: effectiveType }));

          // Cooldown de 5 minutos: Só voltará a mostrar novo balão passado este tempo
          setTimeout(() => (this.slowNetNotified = false), 5 * 60 * 1000);
        }
      }
    }
  }

  private showInternetConnectionError() {
    // Mostra a barra animada de offline apenas uma vez
    if (!this.offlineNotified) {
      this.offlineNotified = true;
      this.networkStatusService.setOffline();
    }
  }

  private handleTimeoutError() {
    this.alert.showError('ERRORS.TIMEOUT');
  }

  private handleValidLogout(err: HttpErrorResponse): boolean {
    if ([401, 403].includes(err.status)) {
      console.error('🔒 [Interceptor] DETETADO 401/403 no request:', err.url);
      console.error('🔒 [Interceptor] Detalhes do Erro (Body):', err.error);

      // 🛡️ EXCEÇÃO: Se estivermos a trocar de NIF, não forçamos o logout total imediato.
      // Deixamos que o LoadDefaultDataService lide com o erro no seu próprio Catch.
      if (this.globalService.isChangeNIFInProgress) {
        console.warn('ℹ️ [Interceptor] 401 ignorado porque isChangeNIFInProgress = true.');
        return true;
      }

      const isLogged = this.authService.isLoggedin(this.globalService.loggedUserClass);
      if (isLogged && !this.isLoggingOut) {
        console.warn('⚠️ [Interceptor] A FORÇAR LOGOUT devido a 401/403 com sessão ativa.');
        this.isLoggingOut = true;
        this.authService.logout(false);

        // Impede que múltiplos 401s mostrem vários alertas
        setTimeout(() => {
          this.isLoggingOut = false;
        }, 5000);
      } else if (!isLogged) {
        console.warn('ℹ️ [Interceptor] 401/403 recebido para utilizador não autenticado:', err.url);
      }
      return true;
    }
    return false;
  }

  /** Verifica se é um erro de rede que deve ser retried (VERDADEIRO erro de rede, não erros da API) */
  private isNetworkError(err: HttpErrorResponse): boolean {
    // Status 0 = falha de rede real (sem conexão, CORS bloqueado no preflight, etc.)
    // MAS: Se recebemos uma resposta com erro (404, 500, etc.) isso NÃO é erro de rede
    if (err.status !== 0) {
      return false; // A API respondeu, não é erro de rede
    }

    // Status 0 com corpo de erro pode ser CORS ou rede
    const errMessage = err.message?.toLowerCase() || '';
    const errStatusText = err.statusText?.toLowerCase() || '';

    return (
      errMessage.includes('unknown error') ||
      errMessage.includes('network') ||
      errMessage.includes('failed to fetch') ||
      errMessage.includes('net::err') ||
      errStatusText === 'unknown error' ||
      errStatusText === 'offline'
    );
  }

  /** Calcula o delay com backoff exponencial */
  private getRetryDelay(attempt: number): number {
    const delay = RETRY_CONFIG.initialDelayMs * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
    return Math.min(delay, RETRY_CONFIG.maxDelayMs);
  }

  /** Handler principal com retry automático para erros de rede */
  private handleRequestWithRetry(
    clonedRequest: HttpRequest<any>,
    next: HttpHandler,
    attempt = 0, // <--- Importante: inicializa o parâmetro aqui
  ): Observable<HttpEvent<any>> {
    // REMOVE a linha 'let retryCount = 0;' que tinhas antes

    const skipError = clonedRequest.headers.has('X-Skip-Error');
    let finalRequest = clonedRequest;

    if (skipError) {
      const headers = clonedRequest.headers.delete('X-Skip-Error');
      finalRequest = clonedRequest.clone({ headers });
    }

    return next.handle(finalRequest).pipe(
      tap(event => this.handleProgress(event)),
      catchError((error: HttpErrorResponse) => {
        // Fix: Resetar RequestProgress em caso de erro HTTP
        // Sem isto, RequestProgress ficava preso em true quando um pedido falhava
        this.globalService.RequestProgress = false;

        // Se skipError for true ou se for um 404 de foto/avatar opcional, não logamos no console.group nem exibimos popup
        const isPhotoOrAvatar404 =
          error.status === 404 &&
          (error.url?.includes('/photo/') || error.url?.includes('/avatar/') || clonedRequest.url?.includes('/photo/'));

        const shouldSkipLog = skipError || isPhotoOrAvatar404;

        if (!shouldSkipLog) {
          console.group(
            '%c🌐 [ERRO API HTTP DETETADO]',
            'background: #dc3545; color: #fff; font-weight: bold; padding: 3px 8px; border-radius: 4px; font-size: 12px;',
          );
          console.error('URL:', error.url);
          console.error('Método:', clonedRequest.method);
          console.error('Status:', error.status, error.statusText);
          console.error('Mensagem:', error.message);
          console.error('Resposta do Servidor (Payload/Body de Erro):', error.error);
          console.error('Rota atual:', typeof window !== 'undefined' ? window.location.pathname : 'SSR');
          console.groupEnd();
        }

        const isExternalThirdParty =
          error.url &&
          (error.url.includes('nominatim.openstreetmap.org') ||
            error.url.includes('bigdatacloud.net') ||
            error.url.includes('tile.openstreetmap.org'));

        // USA 'attempt' para comparar com o limite de 3
        if (this.isNetworkError(error) && !isExternalThirdParty && attempt < RETRY_CONFIG.maxRetries) {
          const delayMs = this.getRetryDelay(attempt);

          console.log(
            `[Retry ${attempt + 1}/${RETRY_CONFIG.maxRetries}] Erro de rede real...`, //
          );

          this.showInternetConnectionError();

          return timer(delayMs).pipe(
            concatMap(() => this.handleRequestWithRetry(finalRequest, next, attempt + 1)), //
          );
        }

        return this.handleError(error, shouldSkipLog);
      }),
    );
  }

  private handleProgress(event: HttpEvent<any>) {
    if (event.type === HttpEventType.DownloadProgress) {
      this.globalService.RequestProgress = true;
    } else if (event.type === HttpEventType.Response) {
      this.globalService.RequestProgress = false;

      // Se obtivemos uma resposta de sucesso, estamos garantidamente online!
      if (!this.networkStatusService.isOnline) {
        // Atualiza o estado da internet se estava marcado como offline
        this.networkStatusService.setOnline();
      }
    }
  }

  private handleError(err: HttpErrorResponse, skipError: boolean = false): Observable<HttpEvent<any>> {
    // Se skipError for true, não logamos erro nem mostramos popup
    if (!skipError) {
      console.error('? API Error:', {
        url: err.url,
        status: err.status,
        statusText: err.statusText,
        message: err.message,
        error: err.error,
      });
    }

    if (err instanceof TimeoutError) {
      this.handleTimeoutError();
      return throwError(() => err);
    }

    if (this.isNetworkError(err)) {
      this.showInternetConnectionError();
      if (!skipError) console.warn('[Rede] Falhou após todas as tentativas de retry');
      return throwError(() => err);
    }

    // Se skipError for true, apenas propagamos o erro sem popup nem logout check (para evitar logs desnecessários em 404s ignoráveis)
    if (skipError) {
      return throwError(() => err);
    }

    if (this.handleValidLogout(err)) {
      return throwError(() => err);
    }

    const errorMessage = HttpclienterrorService.getServerErrorMessage(err);
    // REMOVIDO: Evitar duplicidade com a faixa vermelha de erro
    // this.baseService.PopupMessage(errorMessage, 'X', 'error');
    console.warn('[API Error Suppressed Popup]:', errorMessage);
    return throwError(() => err);
  }
}
